import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'

import { Typography } from 'antd'
import styled from 'styled-components'
import { colours, sizes } from '@qflow/theme'

import {
  AlertsTableWrapper,
  InfractionsTableWrapper
} from 'portal/components/AlertsTable'
import { HorizontalDivider } from 'portal/components/Navigation/Main/Toolbar/Divider'
import { ALERT_TYPES } from 'portal/lib/constants/alertTypes'

import { useUserProjectRole } from 'portal/lib/hooks'
import {
  ColourButton,
  Flex,
  icons,
  SideBar,
  Spacer
} from 'portal/lib/primitives'
import { projectUsersLoad } from 'portal/store/projectUsers/actions'

export const Alerts = ({ startDate, endDate, alertsType }) => {
  // showDismissed is a string because it is stored in localStorage
  const [showDismissed, setShowDismissed] = useState<'true' | 'false'>(
    localStorage.getItem('alert-dismissed-filter')
      ? (localStorage.getItem('alert-dismissed-filter') as 'true' | 'false')
      : 'false'
  )

  const dispatch = useDispatch()

  const { accountId, projectId } = useParams()
  const {
    isProjectUser,
    isReadOnly: isProjectUserReadOnly,
    roleLoaded
  } = useUserProjectRole(accountId, projectId)

  const allowSelectionAndAction = useMemo(() => {
    if (!roleLoaded) {
      // May cause flicker as no loding state in this component
      return false
    }
    return isProjectUser && !isProjectUserReadOnly
  }, [isProjectUser, isProjectUserReadOnly, roleLoaded])

  const { Title } = Typography

  // Load Project Users in to Redux state
  useEffect(() => {
    if (roleLoaded && isProjectUser) {
      dispatch(projectUsersLoad(accountId, projectId, false))
    }
  }, [accountId, dispatch, isProjectUser, projectId, roleLoaded])

  const toggleFilter = () => {
    const newState =
      !showDismissed || showDismissed === 'false' ? 'true' : 'false'
    setShowDismissed(newState)
    localStorage.setItem('alert-dismissed-filter', newState)
  }

  return (
    <>
      <Flex.Row
        style={{ width: '99%', alignItems: 'baseline', flex: '0 0 auto' }}
      >
        <Title level={2} id="Heading" style={{ marginBottom: '0' }}>
          {alertsType} Alerts
        </Title>
        <Flex.By />
        <SideBar.GlobalDatePicker />
      </Flex.Row>
      <HorizontalDivider style={{ width: '99%' }} />
      <FilterButtonContainer>
        <FilterButton
          id="RemovedFilter"
          onClick={toggleFilter}
          $showDismissed={showDismissed}
        >
          <icons.CircleTicked
            size={sizes.SMALL}
            colour={
              showDismissed === 'true'
                ? colours.ORANGE
                : colours.BATTLESHIP_GREY
            }
          />
          <Spacer.Fine />
          {showDismissed === 'true'
            ? 'Including Dismissed'
            : 'Include Dismissed'}
        </FilterButton>
      </FilterButtonContainer>
      {alertsType === ALERT_TYPES.QUALITY_ASSURANCE && (
        <AlertsTableWrapper
          startDate={startDate}
          endDate={endDate}
          showDismissed={showDismissed}
          allowSelectionAndAction={allowSelectionAndAction}
        />
      )}
      {/* Separate tables wrapper for each alert type to prevent filters bleeding
      between them  */}
      {alertsType === ALERT_TYPES.MATERIAL_CERTIFICATIONS && (
        <InfractionsTableWrapper
          startDate={startDate}
          endDate={endDate}
          showDismissed={showDismissed}
          allowSelectionAndAction={allowSelectionAndAction}
          alertsType={alertsType}
        />
      )}
      {alertsType === ALERT_TYPES.WASTE_COMPLIANCE && (
        <InfractionsTableWrapper
          startDate={startDate}
          endDate={endDate}
          showDismissed={showDismissed}
          allowSelectionAndAction={allowSelectionAndAction}
          alertsType={alertsType}
        />
      )}
      {alertsType === ALERT_TYPES.SUBMISSION_ISSUES && (
        <InfractionsTableWrapper
          startDate={startDate}
          endDate={endDate}
          showDismissed={showDismissed}
          allowSelectionAndAction={allowSelectionAndAction}
          alertsType={alertsType}
        />
      )}
    </>
  )
}

const FilterButtonContainer = styled(Flex.Col)`
  display: flex;
  flex-direction: row;
  flex: 0 0 auto;
`
const FilterButton = styled(ColourButton.Basic)<{
  $showDismissed: 'true' | 'false'
}>`
  border-radius: 25px;
  color: ${({ $showDismissed }) =>
    $showDismissed === 'true' ? colours.ORANGE : colours.DARK_GREY};
  background-color: ${({ $showDismissed }) =>
    $showDismissed === 'true' ? '#FFF2EB' : colours.WHITE};
  border-color: ${({ $showDismissed }) =>
    $showDismissed === 'true' ? colours.ORANGE : colours.BORDER_GREY};

  &:hover {
    background: transparent;
    color: ${({ $showDismissed }) =>
      $showDismissed === 'true' ? colours.ORANGE : colours.DARK_GREY};
    border-color: ${({ $showDismissed }) =>
      $showDismissed === 'true' ? colours.ORANGE : colours.DARK_GREY};
  }

  &:focus {
    color: ${({ $showDismissed }) =>
      $showDismissed === 'true' ? colours.ORANGE : colours.DARK_GREY};
    background-color: ${({ $showDismissed }) =>
      $showDismissed === 'true' ? '#FFF2EB' : colours.WHITE};
  }

  &:hover:focus {
    background: transparent;
  }

  > SVG {
    color: ${({ $showDismissed }) =>
      $showDismissed === 'true' ? colours.ORANGE : colours.BATTLESHIP_GREY};
  }

  &:hover > SVG {
    color: ${({ $showDismissed }) =>
      $showDismissed === 'true' ? colours.ORANGE : colours.CHARCOAL_GREY};
  }
`
